import {
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
  OnboardingUserFormValues,
  RoleFormValues,
} from '../../../types';
import {
  BlockActionResult,
  formatUsageScreensMessage,
  getNodeAtPath,
  walkNetworkNodes,
} from '../../networkReferenceUtils';
import {getRoleTypeName} from '../../../../../../../utils';

type RoleTypeOption = {_id: string; name?: {he?: string; en?: string}};

// `roleType` holds a role-type `_id` for API role types, or free text for custom ones.
export const getRoleDisplayName = (
  roleType: string,
  roleTypes: RoleTypeOption[] = [],
): string =>
  getRoleTypeName(
    roleTypes.find(item => item._id === roleType) ?? roleType,
  ).trim() || 'ללא שם';

export type RoleUsage = {
  users: OnboardingUserFormValues[];
};

export function findRoleUsage(
  network: NetworkNodeFormValues[],
  roleId: string,
): RoleUsage {
  const users: OnboardingUserFormValues[] = [];
  const seenUserIds = new Set<string>();

  walkNetworkNodes(network).forEach(({node}) => {
    (node.users ?? []).forEach((user: OnboardingUserFormValues) => {
      if (user.roleId === roleId && !seenUserIds.has(user.id)) {
        seenUserIds.add(user.id);
        users.push(user);
      }
    });
  });

  return {users};
}

export function isRoleInUse(usage: RoleUsage): boolean {
  return usage.users.length > 0;
}

const getRoleUsageScreens = (usage: RoleUsage): string[] =>
  usage.users.length > 0 ? ['משתמשים'] : [];

export function formatRoleDeleteBlockMessage(
  roleName: string,
  usage: RoleUsage,
): string {
  const screens = formatUsageScreensMessage(getRoleUsageScreens(usage));
  return `לא ניתן למחוק את התפקיד "${roleName}" — הוא בשימוש במסכי ${screens}. יש להסיר את השיוכים תחילה`;
}

export function formatRoleReplaceBlockMessage(
  inUse: Array<{role: RoleFormValues; usage: RoleUsage}>,
  roleTypes: RoleTypeOption[] = [],
): string {
  const screens = new Set<string>();
  inUse.forEach(({usage}) => {
    getRoleUsageScreens(usage).forEach(screen => screens.add(screen));
  });

  const roleNames = inUse
    .map(({role}) => `"${getRoleDisplayName(role.roleType, roleTypes)}"`)
    .join(', ');
  return `לא ניתן לייבא, התפקידים ${roleNames} בשימוש במסכי ${formatUsageScreensMessage([...screens])}. יש להסיר את השיוכים תחילה`;
}

export function getInUseRolesOnNode(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
): Array<{role: RoleFormValues; usage: RoleUsage}> {
  const node = getNodeAtPath(network, nodePath);
  if (!node) return [];

  return (node.roles ?? [])
    .map(role => ({
      role,
      usage: findRoleUsage(network, role.id),
    }))
    .filter(({usage}) => isRoleInUse(usage));
}

export function canDeleteRole(
  network: NetworkNodeFormValues[],
  roleId: string,
  roleName: string,
): BlockActionResult {
  const usage = findRoleUsage(network, roleId);
  if (isRoleInUse(usage)) {
    return {
      allowed: false,
      message: formatRoleDeleteBlockMessage(roleName, usage),
    };
  }

  return {allowed: true};
}

export function canReplaceRoles(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
  roleTypes: RoleTypeOption[] = [],
): BlockActionResult {
  const inUse = getInUseRolesOnNode(network, nodePath);

  if (inUse.length === 0) {
    return {allowed: true};
  }

  return {
    allowed: false,
    message: formatRoleReplaceBlockMessage(inUse, roleTypes),
  };
}
